/* eslint-disable max-params, no-mixed-operators */
import { Vector3 } from 'three';

import { easeInOutCubic, EasingFunction, linear } from './easing';
import { interpolateAlongPath, lerpVector3 } from './path-interpolation';

/**
 * Configuration for starting an animation
 */
export interface AnimationConfig {
	/** Unique identifier for the animation */
	id: string;
	/** Duration in milliseconds */
	duration: number;
	/** Easing function (defaults to easeInOutCubic) */
	easing?: EasingFunction;
	/** Called every frame with raw and eased progress (0-1) */
	onUpdate: (progress: number, easedProgress: number) => void;
	/** Called when animation completes */
	onComplete?: () => void;
	/** Optional path for path-based animations */
	path?: Vector3[];
	/** Start position for position-based animations */
	startPosition?: Vector3;
	/** Target position for position-based animations */
	targetPosition?: Vector3;
}

/**
 * Internal animation state
 */
interface Animation {
	id: string;
	startTime: number;
	duration: number;
	easingFn: EasingFunction;
	onUpdate: (progress: number, easedProgress: number) => void;
	onComplete?: () => void;
	path?: Vector3[];
	startPosition?: Vector3;
	targetPosition?: Vector3;
}

/**
 * Generic animation manager for Three.js animations
 *
 * Manages multiple concurrent animations with configurable easing.
 * Call update() in your render loop with the current time.
 *
 * @example
 * ```typescript
 * const manager = new AnimationManager();
 *
 * // Start a simple animation
 * manager.start({
 *   id: 'light-move',
 *   duration: 1000,
 *   onUpdate: (progress, easedProgress) => {
 *     light.position.lerpVectors(start, end, easedProgress);
 *   },
 *   onComplete: () => console.log('Done!')
 * });
 *
 * // In render loop
 * manager.update(performance.now());
 * ```
 */
export class AnimationManager {
	private animations: Map<string, Animation> = new Map();

	private onStart: (() => void) | null = null;

	/**
	 * Static easing functions for convenience
	 */
	static easeInOutCubic = easeInOutCubic;
	static linear = linear;

	/**
	 * Static path interpolation helpers
	 */
	static interpolateAlongPath = interpolateAlongPath;
	static lerpVector3 = lerpVector3;

	/**
	 * Register a callback invoked whenever an animation starts.
	 * The engine uses this to wake its demand-driven render loop: animations
	 * only progress while a rAF loop is ticking update(), so one started while
	 * the loop is asleep would otherwise stay frozen (and its onComplete never
	 * fire) until user interaction restarts the loop.
	 */
	setOnAnimationStart (callback: (() => void) | null): void {
		this.onStart = callback;
	}

	/**
	 * Start a new animation
	 * @param config Animation configuration
	 * @returns The animation ID
	 */
	start (config: AnimationConfig): string {
		const animation: Animation = {
			id: config.id,
			startTime: performance.now(),
			duration: config.duration,
			easingFn: config.easing ?? easeInOutCubic,
			onUpdate: config.onUpdate,
			onComplete: config.onComplete,
			path: config.path,
			startPosition: config.startPosition?.clone(),
			targetPosition: config.targetPosition?.clone()
		};

		// Stop any existing animation with the same ID
		this.stop(config.id);

		this.animations.set(config.id, animation);
		this.onStart?.();
		return config.id;
	}

	/**
	 * Stop an animation by ID
	 * @param id Animation ID to stop
	 * @returns true if animation was found and stopped
	 */
	stop (id: string): boolean {
		return this.animations.delete(id);
	}

	/**
	 * Stop all running animations
	 */
	stopAll (): void {
		this.animations.clear();
	}

	/**
	 * Check if an animation is currently running
	 * @param id Animation ID to check
	 */
	isRunning (id: string): boolean {
		return this.animations.has(id);
	}

	/**
	 * Get current progress of an animation (0-1)
	 * @param id Animation ID
	 * @returns Progress or null if animation not found
	 */
	getProgress (id: string): number | null {
		const animation = this.animations.get(id);
		if (!animation) return null;

		const elapsed = performance.now() - animation.startTime;
		return Math.min(elapsed / animation.duration, 1.0);
	}

	/**
	 * Update all animations - call this in your render loop
	 * @param currentTime Current time in milliseconds (e.g., performance.now())
	 */
	update (currentTime: number): void {
		const completedAnimations: string[] = [];

		for (const [id, animation] of Array.from(this.animations.entries())) {
			const elapsed = currentTime - animation.startTime;
			const rawProgress = Math.min(elapsed / animation.duration, 1.0);
			const easedProgress = animation.easingFn(rawProgress);

			// Call update callback
			animation.onUpdate(rawProgress, easedProgress);

			// Check if animation is complete
			if (rawProgress >= 1.0) {
				completedAnimations.push(id);
				animation.onComplete?.();
			}
		}

		// Remove completed animations
		for (const id of completedAnimations) {
			this.animations.delete(id);
		}
	}

	/**
	 * Get the count of running animations
	 */
	get count (): number {
		return this.animations.size;
	}

	/**
	 * Convenience method to animate a position between two points
	 */
	animatePosition (
		id: string,
		start: Vector3,
		end: Vector3,
		duration: number,
		onPosition: (position: Vector3) => void,
		options?: {
			easing?: EasingFunction;
			onComplete?: () => void;
		}
	): string {
		return this.start({
			id,
			duration,
			easing: options?.easing,
			startPosition: start,
			targetPosition: end,
			onUpdate: (_, easedProgress) => {
				const position = lerpVector3(start, end, easedProgress);
				onPosition(position);
			},
			onComplete: options?.onComplete
		});
	}

	/**
	 * Convenience method to animate along a path
	 */
	animateAlongPath (
		id: string,
		path: Vector3[],
		duration: number,
		onPosition: (position: Vector3) => void,
		options?: {
			easing?: EasingFunction;
			onComplete?: () => void;
		}
	): string {
		return this.start({
			id,
			duration,
			easing: options?.easing,
			path,
			onUpdate: (_, easedProgress) => {
				const position = interpolateAlongPath(path, easedProgress);
				onPosition(position);
			},
			onComplete: options?.onComplete
		});
	}

	/**
	 * Convenience method to animate a numeric value
	 */
	animateValue (
		id: string,
		startValue: number,
		endValue: number,
		duration: number,
		onValue: (value: number) => void,
		options?: {
			easing?: EasingFunction;
			onComplete?: () => void;
		}
	): string {
		return this.start({
			id,
			duration,
			easing: options?.easing,
			onUpdate: (_, easedProgress) => {
				const value = startValue + (endValue - startValue) * easedProgress;
				onValue(value);
			},
			onComplete: options?.onComplete
		});
	}

	/**
	 * Convenience method to animate opacity (fade in/out)
	 * Returns a promise that resolves when animation completes
	 */
	animateOpacity (
		id: string,
		startOpacity: number,
		endOpacity: number,
		duration: number,
		onOpacity: (opacity: number) => void,
		options?: {
			easing?: EasingFunction;
			onComplete?: () => void;
		}
	): Promise<void> {
		return new Promise((resolve) => {
			this.start({
				id,
				duration,
				easing: options?.easing,
				onUpdate: (_, easedProgress) => {
					const opacity = startOpacity + (endOpacity - startOpacity) * easedProgress;
					onOpacity(opacity);
				},
				onComplete: () => {
					options?.onComplete?.();
					resolve();
				}
			});
		});
	}

	/**
	 * Convenience method to animate scale
	 * Returns a promise that resolves when animation completes
	 */
	animateScale (
		id: string,
		startScale: number,
		endScale: number,
		duration: number,
		onScale: (scale: number) => void,
		options?: {
			easing?: EasingFunction;
			onComplete?: () => void;
		}
	): Promise<void> {
		return new Promise((resolve) => {
			this.start({
				id,
				duration,
				easing: options?.easing,
				onUpdate: (_, easedProgress) => {
					const scale = startScale + (endScale - startScale) * easedProgress;
					onScale(scale);
				},
				onComplete: () => {
					options?.onComplete?.();
					resolve();
				}
			});
		});
	}

	/**
	 * Animate position and return a promise
	 */
	animatePositionAsync (
		id: string,
		start: Vector3,
		end: Vector3,
		duration: number,
		onPosition: (position: Vector3) => void,
		options?: {
			easing?: EasingFunction;
		}
	): Promise<void> {
		return new Promise((resolve) => {
			this.animatePosition(id, start, end, duration, onPosition, {
				...options,
				onComplete: resolve
			});
		});
	}
}

